import {
  AfterPaymentError,
  classifyPurchaseError,
  createSubscribeFlow,
  NativeStoreError,
  withFailure,
  type PurchasePresenter,
  type SubscriptionServiceLevelStatus,
} from '@forest/paywall-core'
import { wait } from '@forest/shared'
import { useMutation } from '@tanstack/solid-query'
import {
  closeErrorDialog,
  setErrorDialogLoading,
  showErrorDialog,
} from '~/components/error-dialog'
import { reminderDialog } from '~/components/reminder-dialog'
import { isAbandonedCheckout } from '~/lib/abandoned-checkout'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { openIntercom } from '~/lib/intercom'
import { platform } from '~/lib/params'
import { posthog } from '~/lib/posthog'
import { showRestoreErrorDialog } from '~/lib/restore-purchase'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'
import { m } from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime'
import { api } from '~/services/api'
import { native, type Product } from '@forest/native-services'
import {
  $debugAfterPaymentError,
  $debugSimulateInterrupted,
  $isReminderViewOpen,
  $isTrialEligible,
  $receiptSystemAccountId,
} from '~/store'

const REMINDER_OFFSET_MS = 48 * 60 * 60 * 1000 // 48 hours

function formatReminderDate(expiresAt: string, locale: string): string {
  const expiresDate = new Date(expiresAt)
  const reminderDate = new Date(expiresDate.getTime() - REMINDER_OFFSET_MS)
  return new Intl.DateTimeFormat(locale.replace('_', '-'), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(reminderDate)
}

async function showReminderDialogIfNeeded(expiresAt: string) {
  let permissionResult
  try {
    permissionResult = await native.permission.getHasNotificationPermission()
  } catch {
    return
  }

  if (permissionResult.success && !permissionResult.data) {
    const reminderDate = formatReminderDate(expiresAt, getLocale())
    await reminderDialog.open(reminderDate)
  }
}

const flow = createSubscribeFlow<Product>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  telemetry: {
    captureException: (error, context) =>
      posthog?.captureException(error, context),
  },
  getUserId: async () => (await getAuthenticatedUserInfo()).user_id,
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
  getDebugSimulate: () => $debugAfterPaymentError.get(),
})

const presenter: PurchasePresenter<Product> = {
  success: async (facts) => {
    $isReminderViewOpen.set(false)
    if (
      platform !== 'china_android' &&
      facts.isSubscription &&
      $isTrialEligible.get() &&
      facts.expiresAt
    ) {
      await showReminderDialogIfNeeded(facts.expiresAt)
    }
  },
  error: (intent, { error }) => {
    switch (intent.kind) {
      case 'payment_timeout':
        showErrorDialog({
          title: m.dialog_payment_timeout_title(),
          description: withFailure(
            m.dialog_payment_timeout_content({ error: intent.detail }),
            intent.failure,
          ),
          buttonText: m.OK(),
        })
        return
      case 'illegal_transaction_restore':
        showErrorDialog({
          title: m.dialog_illegal_transaction_title(),
          description: withFailure(
            m.dialog_illegal_transaction_content(),
            intent.failure,
          ),
          buttonText: m.restore_purchase(),
          asyncAction: async () => {
            if (import.meta.env.DEV) {
              setErrorDialogLoading(true)
              await wait(1000)
              closeErrorDialog()
              showErrorDialog({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                buttonText: m.awesome(),
                buttonAction: () => {
                  console.log('close')
                },
              })
              return
            }
            setErrorDialogLoading(true)
            try {
              await flow.restore()
              closeErrorDialog()
              showErrorDialog({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                buttonText: m.awesome(),
                buttonAction: () => {
                  native.user.refreshServiceLevel()
                  native.general.close()
                },
              })
            } catch (restoreError) {
              closeErrorDialog()
              await showRestoreErrorDialog(restoreError)
            }
          },
        })
        return
      case 'http_error':
        showErrorDialog({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          buttonText: m.OK(),
          buttonAction: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_http_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      case 'no_internet':
        showErrorDialog({
          title: m.dialog_no_internet_title(),
          description: withFailure(
            m.dialog_no_internet_content(),
            intent.failure,
          ),
          buttonText: m.OK(),
        })
        return
      case 'subscription_conflict':
        void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
        return
      case 'unknown':
        posthog?.captureException(error, {
          failure_code: intent.failure.code,
        })
        showErrorDialog({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          buttonText: m.OK(),
          buttonAction: () => {
            openIntercom({
              node: 'subscribe_create_purchase_unexpected_error',
              orderId: intent.failure.purchaseId?.toString(),
              additional: { error_code: intent.failure.code },
            })
          },
        })
        return
      default:
        intent satisfies never
    }
  },
}

export function useSubscribeMutation(params?: {
  onSuccess?: (
    serviceLevelStatus: SubscriptionServiceLevelStatus | null,
    product: Product,
  ) => void
}) {
  const { onSuccess } = params ?? {}

  return useMutation(() => ({
    mutationFn: async ({ product }: { product: Product }) => {
      if (import.meta.env.DEV && $debugSimulateInterrupted.get()) {
        throw new NativeStoreError('interrupted')
      }
      if (import.meta.env.DEV && $debugAfterPaymentError.get()) {
        throw new AfterPaymentError(0, '', '')
      }
      return flow.run(product)
    },
    onSuccess: (serviceLevelStatus, { product }) => {
      // Run the success presenter (which may open the reminder dialog) as a
      // detached task so the mutation settles immediately and the loading
      // overlay — bound to isPending — closes before the dialog appears.
      // Awaiting here keeps isPending true for the dialog's whole lifetime,
      // leaving the higher-z-index overlay covering it.
      const task = Promise.resolve(
        presenter.success(flow.handleSuccess(serviceLevelStatus, product)),
      )
      task.then(() => onSuccess?.(serviceLevelStatus, product))
    },
    onError: (error) => {
      if (error instanceof AbortError) return
      const intent = classifyPurchaseError(error)
      // An abandoned checkout is silent — the user closed the store sheet and
      // simply returns to the timeline view (ADR 0030). Genuine timeouts, other
      // store errors, and china_android's uncertain `interrupted` keep the
      // dialog.
      if (isAbandonedCheckout(intent, platform)) return
      presenter.error(intent, { error, flow })
    },
  }))
}
